import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Crown, Skull, Users, TrendingUp, DollarSign, 
  Activity, Eye, EyeOff, LogOut, Building2, Settings,
  BarChart3, Lock, Unlock, RefreshCw, ChevronRight,
  Zap, Heart, MessageCircle, Award, AlertTriangle,
  Calendar, Clock, Globe, Sparkles, Target, Anchor,
  Sword, Ship, Compass, Radio, Rocket, Link2,
  Mail, Gift
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { api } from '../services/api';
import AffiliationAdminDashboard from '../components/admin/AffiliationAdminDashboard';
// 📧 Autorépondeurs - Importés depuis les composants existants
import HeartbeatsDashboard from '../components/admin/HeartbeatsDashboard';
import ProspectsDashboard from '../components/admin/ProspectsDashboard';
// 📊 Dashboard Affiliation Complet
import AffiliationCompleteAdminDashboard from '../components/admin/AffiliationCompleteAdminDashboard';

// Sons spatiaux pirates
const playPirateSound = (type) => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    
    switch (type) {
      case 'beep':
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = 600;
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.1);
        break;
        
      case 'access_granted':
        // Mélodie épique pirate
        const freqs = [392, 523, 659, 784, 1047];
        freqs.forEach((freq, i) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.type = 'sawtooth';
          o.frequency.value = freq;
          g.gain.setValueAtTime(0.08, ctx.currentTime + i * 0.12);
          g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.12 + 0.2);
          o.start(ctx.currentTime + i * 0.12);
          o.stop(ctx.currentTime + i * 0.12 + 0.2);
        });
        break;
        
      case 'error':
        const oscErr = ctx.createOscillator();
        const gainErr = ctx.createGain();
        oscErr.connect(gainErr);
        gainErr.connect(ctx.destination);
        oscErr.type = 'square';
        oscErr.frequency.setValueAtTime(200, ctx.currentTime);
        oscErr.frequency.linearRampToValueAtTime(100, ctx.currentTime + 0.3);
        gainErr.gain.setValueAtTime(0.1, ctx.currentTime);
        gainErr.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscErr.start(ctx.currentTime);
        oscErr.stop(ctx.currentTime + 0.3);
        break;
    }
  } catch (e) {}
};

// Fond spatial pirate avec étoiles et nébuleuses sombres
const PirateSpaceBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    {/* Étoiles */}
    {[...Array(150)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full"
        style={{
          width: Math.random() * 3 + 1,
          height: Math.random() * 3 + 1,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          background: i % 5 === 0 ? '#ff4444' : i % 3 === 0 ? '#ffaa00' : '#ffffff',
        }}
        animate={{
          opacity: [0.3, 1, 0.3],
          scale: [1, 1.5, 1],
        }}
        transition={{
          duration: 1.5 + Math.random() * 2,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      />
    ))}
    
    {/* Nébuleuse rouge sang */}
    <div 
      className="absolute w-[900px] h-[900px] rounded-full opacity-20 blur-[200px]"
      style={{
        background: 'radial-gradient(circle, #ff0000 0%, transparent 70%)',
        left: '-20%',
        top: '-20%',
      }}
    />
    
    {/* Nébuleuse or */}
    <div 
      className="absolute w-[700px] h-[700px] rounded-full opacity-15 blur-[180px]"
      style={{
        background: 'radial-gradient(circle, #ffaa00 0%, transparent 70%)',
        right: '-15%',
        top: '30%',
      }}
    />
    
    {/* Nébuleuse violette sombre */}
    <div 
      className="absolute w-[600px] h-[600px] rounded-full opacity-15 blur-[150px]"
      style={{
        background: 'radial-gradient(circle, #4a0080 0%, transparent 70%)',
        left: '40%',
        bottom: '-10%',
      }}
    />
    
    {/* Grille de vaisseau */}
    <div 
      className="absolute inset-0 opacity-5"
      style={{
        backgroundImage: `
          linear-gradient(to right, #ff4444 1px, transparent 1px),
          linear-gradient(to bottom, #ff4444 1px, transparent 1px)
        `,
        backgroundSize: '100px 100px',
      }}
    />
  </div>
);

// Logo Golem 13 animé
const GolemLogo = () => (
  <motion.div 
    className="relative"
    animate={{ 
      filter: [
        'drop-shadow(0 0 20px rgba(255, 68, 68, 0.5))',
        'drop-shadow(0 0 40px rgba(255, 170, 0, 0.8))',
        'drop-shadow(0 0 20px rgba(255, 68, 68, 0.5))',
      ]
    }}
    transition={{ duration: 2, repeat: Infinity }}
  >
    <div className="text-8xl md:text-9xl font-black text-center">
      <span className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 bg-clip-text text-transparent">
        G13
      </span>
    </div>
    <div className="absolute -top-4 -right-4">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
      >
        <Skull className="w-12 h-12 text-red-500" />
      </motion.div>
    </div>
  </motion.div>
);

// Écran de login Golem 13
const GolemLoginScreen = ({ onLogin }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCode, setShowCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.supremeLogin(code);
      if (response.success) {
        playPirateSound('access_granted');
        toast.success('🏴‍☠️ Bienvenue à bord, Capitaine !');
        onLogin(response);
      }
    } catch (err) {
      playPirateSound('error');
      setError('Code Golem incorrect');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (char) => {
    playPirateSound('beep');
    if (code.length < 10) {
      setCode(code + char);
    }
  };

  const handleDelete = () => {
    playPirateSound('beep');
    setCode(code.slice(0, -1));
  };

  return (
    <div className="min-h-screen bg-[#0a0505] relative overflow-x-hidden overflow-y-auto flex items-center justify-center p-4">
      <PirateSpaceBackground />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-lg"
      >
        {/* Logo et Titre */}
        <div className="text-center mb-8">
          <GolemLogo />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1 className="text-2xl md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 mt-4">
              VAISSEAU MÈRE
            </h1>
            <p className="text-red-500/60 font-mono text-sm mt-2 flex items-center justify-center gap-2">
              <Skull className="w-4 h-4" />
              GOLEM 13 • COMMAND CENTER
              <Skull className="w-4 h-4" />
            </p>
          </motion.div>
        </div>

        {/* Terminal de login */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="relative"
        >
          {/* Cadre lumineux */}
          <div 
            className="absolute -inset-[3px] rounded-3xl opacity-60"
            style={{
              background: 'linear-gradient(135deg, #ff4444, #ffaa00, #ff4444)',
              filter: 'blur(6px)',
            }}
          />
          
          <div 
            className="relative rounded-3xl p-8 border-2 border-red-500/50"
            style={{
              background: 'linear-gradient(180deg, rgba(20, 5, 5, 0.95) 0%, rgba(10, 0, 0, 0.98) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            {/* Header du terminal */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                <span className="text-red-400 font-mono text-sm">ACCÈS COMMANDANT</span>
              </div>
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
              </div>
            </div>

            {/* Affichage du code */}
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-orange-400" />
                <span className="text-orange-400 font-mono text-xs">CODE GOLEM</span>
              </div>
              
              <div className="relative">
                <div className="flex justify-center items-center h-16 rounded-xl bg-[#0a0000] border-2 border-red-500/30 px-4">
                  <span className="text-3xl font-mono tracking-[0.3em] text-orange-400">
                    {showCode ? code : code.replace(/./g, '●')}
                  </span>
                  <motion.span
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    className="text-3xl text-red-500 ml-1"
                  >
                    _
                  </motion.span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCode(!showCode)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Clavier */}
            <div className="grid grid-cols-6 gap-2 mb-6">
              {['G', 'O', 'L', 'E', 'M', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'X', 'Y', 'Z'].map((char) => (
                <motion.button
                  key={char}
                  whileHover={{ scale: 1.1, boxShadow: '0 0 15px rgba(255, 68, 68, 0.5)' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleKeyPress(char)}
                  disabled={loading}
                  className="h-12 rounded-lg font-bold font-mono text-lg bg-gradient-to-br from-red-900/30 to-orange-900/30 border border-red-500/30 text-orange-400 hover:border-red-400 transition-all disabled:opacity-50"
                >
                  {char}
                </motion.button>
              ))}
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-3 mb-4">
              <Button
                type="button"
                onClick={handleDelete}
                variant="outline"
                className="flex-1 h-12 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20 font-bold"
                disabled={loading || code.length === 0}
              >
                ← EFFACER
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || code.length === 0}
                className="flex-1 h-12 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 font-bold text-lg"
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : (
                  <>
                    <Rocket className="w-5 h-5 mr-2" />
                    ACCÉDER
                  </>
                )}
              </Button>
            </div>

            {/* Erreur */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-center text-red-400 text-sm flex items-center justify-center gap-2"
                >
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-red-900/50 text-center">
              <p className="text-red-500/40 text-xs font-mono flex items-center justify-center gap-2">
                <Anchor className="w-3 h-3" />
                SYSTÈME PIRATE GOLEM v13.0
                <Anchor className="w-3 h-3" />
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Dashboard principal Golem 13 (après login)
const GolemDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAffiliationDashboard, setShowAffiliationDashboard] = useState(false);
  // 📧 États pour les autorépondeurs
  const [showHeartbeatsAutoresponder, setShowHeartbeatsAutoresponder] = useState(false);
  const [showProspectsDashboard, setShowProspectsDashboard] = useState(false);
  // 📊 État pour le dashboard affiliation complet
  const [showAffiliationComplete, setShowAffiliationComplete] = useState(false);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [statsData, connectionsData] = await Promise.all([
        api.getSupremeStats?.() || {},
        api.getAdminConnections?.() || { connections: [] }
      ]);
      setStats(statsData);
      setConnections(connectionsData.connections || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="min-h-screen bg-[#0a0505] relative overflow-x-hidden overflow-y-auto">
      <PirateSpaceBackground />
      
      <div className="relative z-10 p-6">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="text-4xl font-black bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
              G13
            </div>
            <div>
              <h1 className="text-xl font-bold text-orange-400">VAISSEAU MÈRE</h1>
              <p className="text-red-500/60 text-xs font-mono">COMMAND CENTER</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={loadData}
              variant="outline"
              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Scanner
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Quitter
            </Button>
          </div>
        </motion.header>

        {/* Stats rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Users, label: 'Clients', value: stats?.totalCustomers || 0, color: 'cyan' },
            { icon: Heart, label: 'Heartbeats', value: stats?.heartbeatsUsers || 0, color: 'pink' },
            { icon: DollarSign, label: 'Revenus', value: `${stats?.totalRevenue || 0}€`, color: 'green' },
            { icon: Activity, label: 'Actifs 24h', value: stats?.active24h || 0, color: 'orange' },
          ].map((stat, idx) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="rounded-2xl p-4 border border-red-500/30 bg-gradient-to-br from-red-900/20 to-orange-900/10"
            >
              <stat.icon className={`w-6 h-6 mb-2 text-${stat.color}-400`} />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-gray-500 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Journal des connexions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-6 border-2 border-red-500/30 bg-gradient-to-br from-[#140505]/90 to-[#0a0000]/90"
        >
          <h2 className="text-xl font-bold text-orange-400 mb-4 flex items-center gap-2">
            <Radio className="w-5 h-5" />
            Journal des Connexions
          </h2>
          
          {connections.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune connexion enregistrée</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {connections.map((conn, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-xl ${
                    conn.success 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {conn.success ? (
                      <Unlock className="w-4 h-4 text-green-400" />
                    ) : (
                      <Lock className="w-4 h-4 text-red-400" />
                    )}
                    <div>
                      <p className="text-white text-sm font-medium">
                        {conn.admin_name || conn.type.replace(/_/g, ' ')}
                      </p>
                      <p className="text-gray-500 text-xs">{conn.admin_role || 'Tentative'}</p>
                    </div>
                  </div>
                  <span className="text-gray-500 text-xs">
                    {new Date(conn.timestamp).toLocaleString('fr-FR')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Accès rapide aux modules */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {[
            { icon: Building2, label: 'Épicerie', path: '/team-lpe/admin', color: 'cyan' },
            { icon: Heart, label: 'Heartbeats', path: '/team-lpe/admin', color: 'pink' },
            { icon: Users, label: 'Clients', path: '/team-lpe/admin', color: 'green' },
            { icon: Link2, label: 'Affiliés', path: 'affiliation', color: 'purple', isModal: true },
          ].map((item, idx) => (
            <motion.button
              key={item.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + idx * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (item.isModal && item.path === 'affiliation') {
                  setShowAffiliationDashboard(true);
                } else if (item.path !== '#') {
                  navigate(item.path);
                }
              }}
              className={`rounded-2xl p-6 border border-red-500/30 bg-gradient-to-br from-red-900/20 to-transparent hover:from-red-900/40 transition-all ${
                item.path === 'affiliation' ? 'border-purple-500/50' : ''
              }`}
            >
              <item.icon className={`w-8 h-8 mx-auto mb-2 text-${item.color}-400`} />
              <p className="text-white font-medium">{item.label}</p>
            </motion.button>
          ))}
        </div>

        {/* 📧 AUTORÉPONDEURS - Section dédiée */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-8"
        >
          <h3 className="text-lg font-bold text-red-400 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            📧 Autorépondeurs
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Autorépondeur Heartbeats */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowHeartbeatsAutoresponder(true)}
              className="rounded-2xl p-6 border border-pink-500/30 bg-gradient-to-br from-pink-900/20 to-purple-900/20 hover:from-pink-900/40 transition-all text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-bold">Heartbeats</h4>
                  <p className="text-pink-300 text-sm">20 emails • 60 jours</p>
                  <p className="text-gray-500 text-xs mt-1">Séquence de bienvenue Heartbeats.fr</p>
                </div>
              </div>
            </motion.button>

            {/* Autorépondeur Bons -10% (LPE) */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowProspectsDashboard(true)}
              className="rounded-2xl p-6 border border-amber-500/30 bg-gradient-to-br from-amber-900/20 to-orange-900/20 hover:from-amber-900/40 transition-all text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-bold">Bons -10% (LPE)</h4>
                  <p className="text-amber-300 text-sm">10 emails • 45 jours</p>
                  <p className="text-gray-500 text-xs mt-1">Séquence La Petite Épicerie</p>
                </div>
              </div>
            </motion.button>
          </div>
        </motion.div>

        {/* 📊 DASHBOARD AFFILIATION COMPLET - Section dédiée */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-8"
        >
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => setShowAffiliationComplete(true)}
            className="w-full rounded-2xl p-6 border-2 border-pink-500/50 bg-gradient-to-br from-pink-900/30 to-purple-900/30 hover:from-pink-900/50 hover:to-purple-900/50 transition-all text-left"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
                  <Link2 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    📊 Dashboard Affiliation Heartbeats
                    <span className="text-xs bg-pink-500 text-white px-2 py-0.5 rounded-full">COMPLET</span>
                  </h3>
                  <p className="text-pink-300 text-sm mt-1">Suivi précis et détaillé du programme d'affiliation</p>
                  <p className="text-gray-500 text-xs mt-1">Affiliés • Commissions • Tracking • Retraits • Statistiques</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-pink-400" />
            </div>
          </motion.button>
        </motion.div>

        {/* Modal Dashboard Affiliation */}
        <AnimatePresence>
          {showAffiliationDashboard && (
            <AffiliationAdminDashboard onClose={() => setShowAffiliationDashboard(false)} />
          )}
        </AnimatePresence>

        {/* 📧 Modal Autorépondeur Heartbeats */}
        <AnimatePresence>
          {showHeartbeatsAutoresponder && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/90 overflow-y-auto"
            >
              <div className="max-w-6xl mx-auto p-4">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Heart className="w-8 h-8 text-pink-500" />
                    Autorépondeur Heartbeats
                  </h2>
                  <Button
                    variant="ghost"
                    onClick={() => setShowHeartbeatsAutoresponder(false)}
                    className="text-white hover:bg-white/10"
                  >
                    ✕ Fermer
                  </Button>
                </div>
                <HeartbeatsDashboard />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 📧 Modal Autorépondeur Bons -10% (LPE) */}
        <AnimatePresence>
          {showProspectsDashboard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/90 overflow-y-auto"
            >
              <div className="max-w-6xl mx-auto p-4">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Gift className="w-8 h-8 text-amber-500" />
                    Autorépondeur Bons -10% (LPE)
                  </h2>
                  <Button
                    variant="ghost"
                    onClick={() => setShowProspectsDashboard(false)}
                    className="text-white hover:bg-white/10"
                  >
                    ✕ Fermer
                  </Button>
                </div>
                <ProspectsDashboard />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 📊 Modal Dashboard Affiliation Complet */}
        <AnimatePresence>
          {showAffiliationComplete && (
            <AffiliationCompleteAdminDashboard onClose={() => setShowAffiliationComplete(false)} />
          )}
        </AnimatePresence>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center mt-12 text-red-500/30 text-xs font-mono"
        >
          <p className="flex items-center justify-center gap-2">
            <Skull className="w-4 h-4" />
            GOLEM 13 • VAISSEAU MÈRE PIRATE • v13.0
            <Skull className="w-4 h-4" />
          </p>
        </motion.footer>
      </div>
    </div>
  );
};

// Page principale Supreme Admin
export default function SupremeAdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const handleLogin = (data) => {
    setIsAuthenticated(true);
    localStorage.setItem('golem13_access', 'true');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('golem13_access');
    navigate('/');
  };

  // Vérifier si déjà connecté
  useEffect(() => {
    const access = localStorage.getItem('golem13_access');
    if (access === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  if (!isAuthenticated) {
    return <GolemLoginScreen onLogin={handleLogin} />;
  }

  return <GolemDashboard onLogout={handleLogout} />;
}
